import { Button } from '@ads/components-react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import illustrationEmpty from '@/assets/svg/notifications-empty.svg';
import illustrationError from '@/assets/svg/notifications-error.svg';
import illustrationNoResult from '@/assets/svg/notifications-no-result.svg';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';
import { useNotificationCancel } from '../../../hooks/useNotificationCancel';
import type {
  NotificationFilters as NotificationFiltersType,
  NotificationItem,
} from '../../../types/notification';
import { NotificationCancelDialog } from '../NotificationCancelDialog';
import { NotificationDetailDrawer } from '../NotificationDetailDrawer';
import { NotificationFilters } from '../NotificationFilters';
import { NotificationTable } from '../NotificationTable';

export interface NotificationListPanelProps {
  readonly filters: NotificationFiltersType;
  readonly onFiltersChange: (partial: Partial<NotificationFiltersType>) => void;
  readonly data: readonly NotificationItem[];
  readonly isLoading: boolean;
  readonly isFetching?: boolean;
  readonly isError?: boolean;
  readonly onRetry?: () => void;
  readonly totalCount: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly onEdit?: (item: NotificationItem) => void;
  readonly onCreateNew?: () => void;
}

/**
 * Painel composto de filtros + tabela da listagem de notificações.
 *
 * Encapsula o layout do card com filtros, tabela, drawer de detalhes
 * e modal de confirmação de cancelamento.
 */
export function NotificationListPanel({
  filters,
  onFiltersChange,
  data,
  isLoading,
  isFetching = false,
  isError = false,
  onRetry,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onEdit,
  onCreateNew,
}: NotificationListPanelProps) {
  const { t } = useTranslation('notification');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<NotificationItem | null>(null);
  const [cancelItem, setCancelItem] = useState<NotificationItem | null>(null);

  const { mutate: cancelNotification, isPending: isCancelling } = useNotificationCancel();

  const handleRowClick = useCallback((item: NotificationItem) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  }, []);

  const handleCancelClick = useCallback((item: NotificationItem) => {
    setCancelItem(item);
  }, []);

  const handleCancelClose = useCallback(() => {
    if (!isCancelling) setCancelItem(null);
  }, [isCancelling]);

  const handleCancelConfirm = useCallback(() => {
    if (!cancelItem) return;
    cancelNotification(
      { notificationId: Number(cancelItem.id) },
      { onSuccess: () => setCancelItem(null) },
    );
  }, [cancelItem, cancelNotification]);

  const hasActiveSearch = !!(
    filters.search ||
    filters.sentBy ||
    filters.classroomId ||
    filters.statuses.length > 0 ||
    filters.startDate ||
    filters.endDate
  );

  const emptyStateIllustration = hasActiveSearch ? illustrationNoResult : illustrationEmpty;

  const emptyStateTitle = hasActiveSearch
    ? t('table.emptySearchTitle')
    : t('table.emptyFirstUseTitle');

  const emptyStateDescription = hasActiveSearch
    ? t('table.emptySearchDescription')
    : t('table.emptyFirstUseDescription');

  const emptyStateAction = onCreateNew ? (
    <Button hierarchy="primary" size="md" onClick={onCreateNew}>
      {t('actions.new')}
    </Button>
  ) : undefined;

  return (
    <>
      <div className="overflow-hidden rounded-md">
        <div className="relative rounded-md border-hairline my-xxs border-ctx-layout-border bg-ctx-layout-surface p-mini">
          <NotificationFilters
            filters={filters}
            onFiltersChange={onFiltersChange}
            isSearching={isFetching}
          />
        </div>
        <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface">
          {isError ? (
            <EmptyState
              illustration={illustrationError}
              title={t('error.title')}
              description={t('error.description')}
              action={
                onRetry ? (
                  <Button leadingIcon={ArrowPathIcon} size="md" onClick={onRetry}>
                    {t('error.retry')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <NotificationTable
              data={data}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onRowClick={handleRowClick}
              onEdit={onEdit}
              onCancel={handleCancelClick}
              emptyStateTitle={emptyStateTitle}
              emptyStateDescription={emptyStateDescription}
              emptyStateIllustration={emptyStateIllustration}
              emptyStateAction={emptyStateAction}
            />
          )}
        </div>
      </div>

      <NotificationDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={selectedItem}
      />

      <NotificationCancelDialog
        open={cancelItem !== null}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
        isConfirming={isCancelling}
      />
    </>
  );
}
